/**
 * Date helpers. The API stores UTC; retreats carry their own display timezone
 * (Europe/Zagreb before the trip, Indian/Maldives on the island).
 */

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

/** "12 – 19 May 2026" for a retreat's start and end date. */
export function formatRetreatDates({ starts_on: startsOn, ends_on: endsOn }) {
  const start = new Date(`${startsOn}T00:00:00Z`)
  const end = new Date(`${endsOn}T00:00:00Z`)

  const sameMonth = start.getUTCMonth() === end.getUTCMonth()
  const startPart = sameMonth
    ? `${start.getUTCDate()}`
    : `${start.getUTCDate()} ${MONTHS[start.getUTCMonth()]}`

  return `${startPart} – ${end.getUTCDate()} ${MONTHS[end.getUTCMonth()]} ${end.getUTCFullYear()}`
}

/** Whole days from today to a YYYY-MM-DD date; negative once it has passed. */
export function daysUntil(date) {
  const target = new Date(`${date}T00:00:00Z`)
  const now = new Date()
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())

  return Math.round((target.getTime() - today) / 86400000)
}

/** Parses the API's "YYYY-MM-DD HH:MM:SS" (always UTC) into a Date. */
export function parseUtc(value) {
  return new Date(value.replace(' ', 'T') + 'Z')
}

/** "14 May at 19:00" in the retreat's own timezone. */
export function formatMoment(utc, timeZone) {
  const date = parseUtc(utc)
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date)

  const get = (type) => parts.find((part) => part.type === type)?.value ?? ''

  return `${get('day')} ${get('month')} at ${get('hour')}:${get('minute')}`
}

/** "opens in 3 days" / "opens today" for a locked timeline point. */
export function opensIn(utc, now = new Date()) {
  const diffMs = parseUtc(utc).getTime() - now.getTime()
  const days = Math.ceil(diffMs / 86400000)

  if (diffMs <= 0) return 'opens shortly'
  if (days <= 0) return 'opens today'
  if (days === 1) return 'opens tomorrow'

  return `opens in ${days} days`
}

/**
 * Which calendar day a UTC moment falls on *in the retreat's timezone*, as a
 * day number. Comparing these instead of millisecond differences is what makes
 * "Danas" mean today on the island rather than today in UTC.
 */
function zonedDay(date, timeZone) {
  const [year, month, day] = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
    .format(date)
    .split('-')
    .map(Number)

  return Date.UTC(year, month - 1, day) / 86400000
}

/** Whole days from today to a UTC moment, in the retreat's timezone. */
export function dayDelta(utc, timeZone, now = new Date()) {
  return zonedDay(parseUtc(utc), timeZone) - zonedDay(now, timeZone)
}

/** "12 May" — the date without a year or a time. */
export function dayMonth(utc, timeZone) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    day: 'numeric',
    month: 'long',
  }).formatToParts(parseUtc(utc))

  const get = (type) => parts.find((part) => part.type === type)?.value ?? ''

  return `${get('day')} ${get('month')}`
}

/**
 * "Yesterday" / "Today" / "Tomorrow" / "In 3 days" — and the plain date once the
 * distance is too great for a relative word to help.
 */
export function relativeDay(utc, timeZone, now = new Date()) {
  const delta = dayDelta(utc, timeZone, now)

  if (delta === 0) return 'Today'
  if (delta === 1) return 'Tomorrow'
  if (delta === -1) return 'Yesterday'
  if (delta > 1 && delta <= 6) return `In ${delta} days`
  if (delta < -1 && delta >= -6) return `${-delta} days ago`

  return dayMonth(utc, timeZone)
}

/** "07:00" in the retreat's timezone, for a lock that opens today or tomorrow. */
export function timeOfDay(utc, timeZone) {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(parseUtc(utc))
}

/**
 * What a locked point says about itself: the clock when it is close enough for
 * the hour to matter, the date when it is not.
 */
export function lockHint(utc, timeZone, now = new Date()) {
  const delta = dayDelta(utc, timeZone, now)

  return delta <= 1 ? `Opens at ${timeOfDay(utc, timeZone)}` : dayMonth(utc, timeZone)
}

/**
 * "SEP 09", or "TODAY" when it is. The mono stamp the timeline and the player
 * use — short enough to sit in a corner, specific enough to place a day.
 */
export function stamp(utc, timeZone, now = new Date()) {
  if (dayDelta(utc, timeZone, now) === 0) {
    return 'TODAY'
  }

  // en-US rather than en-GB: British short months give "Sept", and the stamp
  // only works as a stamp when every month is three letters.
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    day: '2-digit',
    month: 'short',
  }).formatToParts(parseUtc(utc))

  const get = (type) => parts.find((part) => part.type === type)?.value ?? ''

  return `${get('month').toUpperCase()} ${get('day')}`
}

/** Short label for the date column of the timeline, in the retreat timezone. */
export function shortDate(utc, timeZone) {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone,
    day: 'numeric',
    month: 'numeric',
  }).format(parseUtc(utc))
}

/** Days, hours and minutes left until a UTC timestamp, or null once it passes. */
export function countdown(utc, now = new Date()) {
  const diff = parseUtc(utc).getTime() - now.getTime()

  if (diff <= 0) {
    return null
  }

  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff % 86400000) / 3600000),
    minutes: Math.floor((diff % 3600000) / 60000),
  }
}

/** "4 August 2026" — a date without a time, for locks and entries. */
export function formatDay(utc, timeZone = 'Europe/Zagreb') {
  const date = utc.length === 10 ? new Date(`${utc}T00:00:00Z`) : parseUtc(utc)

  return new Intl.DateTimeFormat('en-GB', {
    timeZone,
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date)
}
